<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { Button, Input, PageHeader, Section, Spinner, Stat, Table, Tabs, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { getGa4Behavior } from '../api/analytics.js'
import Ga4TrafficChart from '../components/Ga4TrafficChart.vue'

const { t } = useI18n()
const filters = reactive({ since: day(-29), until: day(0) })
const report = ref(null)
const loading = ref(false)
const error = ref('')
const activeTab = ref('traffic')
const summaryKeys = ['unique_visitors', 'page_views', 'active_users', 'sessions', 'video_viewers', 'video_starts', 'video_completions', 'ui_clicks', 'scene_clicks', 'typed_inputs', 'selected_inputs']
const columnWidths = Object.freeze({
  path: '320px', title: '240px', views: '100px', users: '100px',
  from: '360px', to: '320px', category: '120px', operating_system: '180px', browser: '180px', sessions: '100px',
  country: '220px', region: '240px',
  video_id: '260px', video_title: '280px', video_url: '120px', placement: '140px', starts: '120px', progress_events: '180px', completions: '120px',
  element_id: '240px', surface: '180px', clicks: '100px', scene_id: '240px', grid_x: '100px', grid_y: '100px',
  pointer_type: '120px', input_method: '180px', mode: '120px', submissions: '110px',
  channel: '180px', source_medium: '260px', campaign: '220px', note: '420px', action: '220px', area: '220px', date: '160px', unique_visitors: '160px', page_views: '160px',
})
const reportTabs = computed(() => ['traffic', 'sources', 'locations', 'pages', 'flows', 'devices', 'videos', 'clicks', 'inputs'].map(key => ({ key, label: t(`visualGameAdmin.ga4Behavior.sections.${key}`) })))
const sourceRows = computed(() => report.value === null ? [] : report.value.sources.map(row => ({ ...row, note: t(`visualGameAdmin.ga4Behavior.sourceNotes.${row.note_key}`) })))
const clickRows = computed(() => report.value === null ? [] : report.value.clicks.map(row => {
  const action = clickLabel('elements', row.element_id)
  const area = clickLabel('surfaces', row.surface)
  return { ...row, action, area, note: t('visualGameAdmin.ga4Behavior.clickNote', { area, action }) }
}))
const videoRows = computed(() => report.value === null ? [] : report.value.videos.map(row => ({
  ...row,
  video_title: row.video_title === null ? t('visualGameAdmin.ga4Behavior.videoUnavailable') : row.video_title,
  placement: videoLabel('placements', row.placement),
})))
const inputRows = computed(() => report.value === null ? [] : report.value.inputs.map(row => ({
  ...row,
  input_method: inputLabel('methods', row.input_method),
  mode: inputLabel('modes', row.mode),
})))
const sourceColumns = computed(() => columns(['channel', 'source_medium', 'campaign', 'note', 'sessions', 'users']))
const trafficColumns = computed(() => columns(['date', 'unique_visitors', 'page_views']))
const pageColumns = computed(() => columns(['path', 'title', 'views', 'users']))
const flowColumns = computed(() => columns(['from', 'to', 'views', 'users']))
const deviceColumns = computed(() => columns(['category', 'operating_system', 'browser', 'users', 'sessions']))
const locationColumns = computed(() => columns(['country', 'region', 'users', 'sessions', 'views']))
const videoColumns = computed(() => columns(['video_title', 'video_url', 'placement', 'starts', 'progress_events', 'completions', 'users']))
const clickColumns = computed(() => columns(['action', 'area', 'note', 'clicks', 'users']))
const inputColumns = computed(() => columns(['input_method', 'mode', 'submissions', 'users']))

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    report.value = await getGa4Behavior({ since: iso(filters.since), until: isoNext(filters.until) })
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function columns(keys) {
  return keys.map(key => {
    const width = columnWidths[key]
    if (!width) throw new Error(`GA4 behavior table column width is missing: ${key}`)
    return {
      key,
      label: t(`visualGameAdmin.ga4Behavior.columns.${key}`),
      width,
      align: ['unique_visitors', 'page_views', 'views', 'users', 'sessions', 'starts', 'progress_events', 'completions', 'clicks', 'submissions'].includes(key) ? 'right' : 'left',
      mono: ['unique_visitors', 'page_views', 'views', 'users', 'sessions', 'starts', 'progress_events', 'completions', 'clicks', 'submissions'].includes(key),
    }
  })
}

function clickLabel(group, value) {
  const key = `visualGameAdmin.ga4Behavior.clickLabels.${group}.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

function inputLabel(group, value) {
  const key = `visualGameAdmin.ga4Behavior.inputLabels.${group}.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

function videoLabel(group, value) {
  const key = `visualGameAdmin.ga4Behavior.videoLabels.${group}.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

function day(offset) {
  const value = new Date()
  value.setHours(0, 0, 0, 0)
  value.setDate(value.getDate() + offset)
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

function iso(value) {
  const date = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) throw new Error(`Invalid GA4 analytics date: ${value}`)
  return date.toISOString()
}

function isoNext(value) {
  const date = new Date(iso(value))
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString()
}
</script>

<template>
  <div class="ga4-behavior vga-page">
    <PageHeader :title="t('visualGameAdmin.ga4Behavior.title')" :description="t('visualGameAdmin.ga4Behavior.description')" />
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field"><label>{{ t('visualGameAdmin.analytics.since') }}</label><Input v-model="filters.since" type="date" size="sm" /></div>
        <div class="drama-toolbar-field"><label>{{ t('visualGameAdmin.analytics.until') }}</label><Input v-model="filters.until" type="date" size="sm" /></div>
        <template #actions><Button :loading="loading" @click="load">{{ t('visualGameAdmin.analytics.refresh') }}</Button></template>
      </Toolbar>
    </Section>
    <div v-if="loading && report === null" class="vga-state"><Spinner /></div>
    <div v-else-if="error" class="vga-state"><p class="vga-error">{{ error }}</p></div>
    <div v-else-if="report !== null && !report.configured" class="ga4-behavior__setup">
      <h2>{{ t('visualGameAdmin.ga4Behavior.setupTitle') }}</h2>
      <p>{{ t('visualGameAdmin.ga4Behavior.setupBody') }}</p>
      <code>GA4_PROPERTY_ID</code>
      <code>GOOGLE_APPLICATION_CREDENTIALS</code>
    </div>
    <template v-else-if="report !== null">
      <div class="ga4-behavior__stats">
        <Stat v-for="key in summaryKeys" :key="key" :label="t(`visualGameAdmin.ga4Behavior.summary.${key}`)" :value="report.summary[key]" />
      </div>
      <Section>
        <Tabs v-model="activeTab" :tabs="reportTabs">
          <template #traffic><Ga4TrafficChart :items="report.traffic_daily" /><Table :columns="trafficColumns" :rows="report.traffic_daily" /></template>
          <template #sources><Table :columns="sourceColumns" :rows="sourceRows" /></template>
          <template #locations><Table :columns="locationColumns" :rows="report.locations" /></template>
          <template #pages><Table :columns="pageColumns" :rows="report.pages" /></template>
          <template #flows><p class="ga4-behavior__hint">{{ t('visualGameAdmin.ga4Behavior.flowHint') }}</p><Table :columns="flowColumns" :rows="report.flows" /></template>
          <template #devices><Table :columns="deviceColumns" :rows="report.devices" /></template>
          <template #videos>
            <p class="ga4-behavior__hint">{{ t('visualGameAdmin.ga4Behavior.videoHint') }}</p>
            <Table :columns="videoColumns" :rows="videoRows">
              <template #video_url="{ row }">
                <a v-if="row.video_url !== null" class="ga4-behavior__video-link" :href="row.video_url" target="_blank" rel="noreferrer">{{ t('visualGameAdmin.ga4Behavior.videoOpen') }}</a>
                <span v-else>{{ t('visualGameAdmin.ga4Behavior.videoUnavailable') }}</span>
              </template>
              <template #starts="{ row }">{{ t(`visualGameAdmin.ga4Behavior.${row.starts === 0 ? 'videoStartNone' : 'videoStartCount'}`, { count: row.starts }) }}</template>
              <template #progress_events="{ row }">{{ t(`visualGameAdmin.ga4Behavior.${row.progress_events === 0 ? 'videoProgressNone' : 'videoProgressCount'}`, { count: row.progress_events }) }}</template>
              <template #completions="{ row }">{{ t(`visualGameAdmin.ga4Behavior.${row.completions === 0 ? 'videoCompletionNone' : 'videoCompletionCount'}`, { count: row.completions }) }}</template>
              <template #users="{ row }">{{ t('visualGameAdmin.ga4Behavior.videoUserCount', { count: row.users }) }}</template>
            </Table>
          </template>
          <template #clicks><Table :columns="clickColumns" :rows="clickRows" /></template>
          <template #inputs><Table :columns="inputColumns" :rows="inputRows" /></template>
        </Tabs>
      </Section>
    </template>
  </div>
</template>

<style scoped>
.ga4-behavior__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: var(--drama-space-md); }
.ga4-behavior__setup { display: grid; gap: var(--drama-space-md); padding: var(--drama-space-2xl); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); }
.ga4-behavior__setup h2, .ga4-behavior__setup p { margin: 0; }
.ga4-behavior__setup code { width: max-content; padding: var(--drama-space-xs) var(--drama-space-sm); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); }
.ga4-behavior__hint { margin: 0 0 var(--drama-space-md); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.ga4-behavior__video-link { color: var(--drama-color-primary); text-decoration: none; }
.ga4-behavior__video-link:hover { text-decoration: underline; }
</style>
